import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { InventoryBoxSplitCombineModal } from "@/components/inventory/InventoryBoxTransferCards";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import type { InventoryBox } from "@/lib/inventoryBoxApi";
import type { ZplPrintDocument } from "@/lib/zplPrintLocation";

export function KittingInventoryBoxPickModal({
  box,
  printer,
  station = "",
  onStationChange,
  documents = [],
  unavailableMessage,
  actionLabel = "Pick",
  onCancel,
  onPickQuantity,
}: {
  box: InventoryBox | null;
  printer: string;
  station?: string;
  onStationChange?: (station: string) => void;
  documents?: readonly ZplPrintDocument[];
  unavailableMessage?: string | null;
  actionLabel?: "Pick" | "Pack";
  onCancel: () => void;
  onPickQuantity: (
    sku: string,
    quantity: number,
    inventoryBoxHexes: string[],
    unpickedInventoryBoxHexes?: string[],
  ) => void;
}): JSX.Element | null {
  const [splitting, setSplitting] = useState(false);

  useEffect(() => {
    setSplitting(false);
  }, [box]);

  if (box == null) return null;

  if (splitting) {
    return (
      <InventoryBoxSplitCombineModal
        open
        mode="split"
        boxHex={box.hex}
        boxShortCode={box.short_code}
        unitKind={box.unit_kind}
        quantity={box.quantity}
        selectedPrinter={printer}
        station={station}
        onStationChange={onStationChange}
        documents={documents}
        unavailableMessage={unavailableMessage}
        pickActionLabel={actionLabel}
        onPickedAfterSplit={(pickedQuantity, selectedHexes, unpickedHexes) => {
          onPickQuantity(box.sku, pickedQuantity, selectedHexes, unpickedHexes);
        }}
        onClose={() => setSplitting(false)}
      />
    );
  }

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-2 w-full max-w-2xl shadow-xl">
        <CardHeader className="border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {actionLabel} {box.short_code}: {box.sku}
          </h2>
          <p className="text-sm text-slate-600">
            This {box.unit_kind} contains {String(box.quantity)}.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-700">
            {actionLabel} the entire container, or split it into smaller labeled containers.
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button type="button" variant="secondary" onClick={() => setSplitting(true)}>
              Split container
            </Button>
            <Button type="button" onClick={() => onPickQuantity(box.sku, box.quantity, [box.hex])}>
              {actionLabel} all {String(box.quantity)}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
